export const generateImageThumbnailFromUrl = async (
  downloadUrl: string,
  size: number = 250,
  quality: number = 0.8
): Promise<string> => {
  return generateImageThumbnail(undefined, downloadUrl, undefined, size, quality);
};

export const generateImageThumbnailFromBlob = async (
  blob: Blob,
  size: number = 250,
  quality: number = 0.8
): Promise<string> => {
  return generateImageThumbnail(undefined, undefined, blob, size, quality);
};

export const generateImageThumbnailFromFile = async (
  file: File,
  size: number = 250,
  quality: number = 0.8
): Promise<string> => {
  return generateImageThumbnail(file, undefined, undefined, size, quality);
};

/**
 * Generates a thumbnail from an image file preserving aspect ratio
 * @param file - Original image file
 * @param downloadUrl - Download URL of the image file
 * @param blob - Blob of the image file
 * @param maxSize - Maximum dimension for thumbnail (default 250)
 * @param quality - WebP quality 0-1 (default 0.8)
 * @returns Promise with thumbnail blob URL
 */
export const generateImageThumbnail = async (
  file?: File,
  downloadUrl?: string,
  blob?: Blob,
  maxSize: number = 250,
  quality: number = 0.8
): Promise<string> => {
  return new Promise((resolve, reject) => {

    if (!file && !downloadUrl && !blob) {
      reject(new Error('No image source provided'));
      return;
    }

    let objectUrl: string;
    let shouldRevoke = false;

    if (file) {
      objectUrl = URL.createObjectURL(file);
      shouldRevoke = true;
    } else if (blob) {
      objectUrl = URL.createObjectURL(blob);
      shouldRevoke = true;
    } else {
      objectUrl = downloadUrl!;
    }

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        // Clean up the temporary object URL
        if (shouldRevoke)
          URL.revokeObjectURL(objectUrl);

        // Calculate dimensions preserving aspect ratio
        const aspectRatio = img.width / img.height;
        let width: number;
        let height: number;

        if (img.width > img.height) {
          width = maxSize;
          height = Math.round(maxSize / aspectRatio);
        } else {
          height = maxSize;
          width = Math.round(maxSize * aspectRatio);
        }

        // Create canvas for thumbnail
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }

        // Draw image scaled to fit
        ctx.drawImage(img, 0, 0, width, height);

        // Convert to WebP blob
        canvas.toBlob(
          (blob) => {
            if (blob) {
              const thumbnailUrl = URL.createObjectURL(blob);
              resolve(thumbnailUrl);
            } else {
              reject(new Error('Failed to create thumbnail blob'));
            }
          },
          'image/webp',
          quality
        );
      } catch (error) {
        reject(error);
      }
    };

    img.onerror = () => {
      if (shouldRevoke)
        URL.revokeObjectURL(objectUrl);
      reject(new Error('Failed to load image'));
    };

    img.src = objectUrl;
  });
};

export const generateVideoThumbnailFromUrl = async (
  downloadUrl: string,
  size: number = 250,
  quality: number = 0.8,
  timeOffset: number = 1
): Promise<string> => {
  return generateVideoThumbnail(undefined, downloadUrl, undefined, size, quality, timeOffset);
};

export const generateVideoThumbnailFromFile = async (
  file: File,
  size: number = 250,
  quality: number = 0.8,
  timeOffset: number = 1
): Promise<string> => {
  return generateVideoThumbnail(file, undefined, undefined, size, quality, timeOffset);
};

export const generateVideoThumbnailFromBlob = async (
  blob: Blob,
  size: number = 250,
  quality: number = 0.8,
  timeOffset: number = 1
): Promise<string> => {
  return generateVideoThumbnail(undefined, undefined, blob, size, quality, timeOffset);
};

/**
 * Generates a thumbnail from a video file preserving aspect ratio
 * @param file - Original video file
 * @param downloadUrl - Download URL of the video file
 * @param blob - Blob of the video file
 * @param maxSize - Maximum dimension for thumbnail (default 250)
 * @param quality - WebP quality 0-1 (default 0.8)
 * @param timeOffset - Time in seconds to capture frame (default 1)
 * @returns Promise with thumbnail blob URL
 */
export const generateVideoThumbnail = async (
  file?: File,
  downloadUrl?: string,
  blob?: Blob,
  maxSize: number = 250,
  quality: number = 0.8,
  timeOffset: number = 1
): Promise<string> => {
  return new Promise((resolve, reject) => {

    if (!file && !downloadUrl && !blob) {
      reject(new Error('No video source provided'));
      return;
    }

    let objectUrl: string;
    let shouldRevoke = false;

    if (file) {
      objectUrl = URL.createObjectURL(file);
      shouldRevoke = true;
    } else if (blob) {
      objectUrl = URL.createObjectURL(blob);
      shouldRevoke = true;
    } else {
      objectUrl = downloadUrl!;
    }

    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';

    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;

    video.onloadedmetadata = () => {
      // Seek to the specified time or 1 second (or start if video is shorter)
      video.currentTime = Math.min(timeOffset, video.duration * 0.1);
    };

    video.onseeked = () => {
      try {
        if (shouldRevoke)
          URL.revokeObjectURL(objectUrl);

        // Calculate dimensions preserving aspect ratio
        const aspectRatio = video.videoWidth / video.videoHeight;
        let width: number;
        let height: number;

        if (video.videoWidth > video.videoHeight) {
          width = maxSize;
          height = Math.round(maxSize / aspectRatio);
        } else {
          height = maxSize;
          width = Math.round(maxSize * aspectRatio);
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }

        // Draw video frame scaled to fit
        ctx.drawImage(video, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              const thumbnailUrl = URL.createObjectURL(blob);
              resolve(thumbnailUrl);
            } else {
              reject(new Error('Failed to create thumbnail blob'));
            }
          },
          'image/webp',
          quality
        );
      } catch (error) {
        reject(error);
      }
    };

    video.onerror = () => {
      if (shouldRevoke)
        URL.revokeObjectURL(objectUrl);
      reject(new Error('Failed to load video'));
    };

    video.src = objectUrl;
  });
};
